import type { LucideIcon, LucideProps } from 'lucide-react'
import { useState, forwardRef } from 'react'

import { cn } from 'src/shared/utils/cn'

export const CheckIcon: LucideIcon = forwardRef<SVGSVGElement, LucideProps>(({ size = 10, ...props }, ref) => {
  return (
    <svg
      ref={ref}
      width={size}
      height={(+size * 7) / 10}
      viewBox="0 0 10 7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8.33887 0.568359C8.42993 0.477451 8.57683 0.477459 8.66699 0.567383V0.568359C8.75777 0.659128 8.75819 0.805868 8.66797 0.896484L3.4873 6.07715V6.07812C3.44209 6.12335 3.38273 6.14551 3.32227 6.14551C3.26231 6.14541 3.2037 6.12265 3.1582 6.07715L0.568359 3.4873C0.477491 3.39644 0.477491 3.24907 0.568359 3.1582C0.659243 3.06752 0.806653 3.0674 0.897461 3.1582L3.32324 5.58398L8.33887 0.568359Z"
        fill="currentColor"
        stroke="currentColor"
      />
    </svg>
  )
})

CheckIcon.displayName = 'CheckIcon'

interface ConsentCheckboxButtonProps {
  isChecked: boolean
  ariaLabel: string
  onToggle: () => void
}

function ConsentCheckboxButton({ isChecked, ariaLabel, onToggle }: ConsentCheckboxButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={isChecked}
      onClick={(event) => {
        event.stopPropagation()
        onToggle()
      }}
      className={cn(
        'mt-[2px] flex size-[20px] shrink-0 items-center justify-center rounded-full border border-border bg-white text-border',
        isChecked && 'border-primary bg-primary text-white',
      )}
    >
      <CheckIcon />
    </button>
  )
}

export default function PhoneNumberSmsConsent({ className }: { className?: string }) {
  const [transactionalSmsChecked, setTransactionalSmsChecked] = useState(false)
  const [marketingSmsChecked, setMarketingSmsChecked] = useState(false)

  return (
    <div className={cn('flex w-full flex-col', className)}>
      <div className="flex flex-col gap-[10px]">
        <div
          onClick={() => setTransactionalSmsChecked((prev) => !prev)}
          className="flex cursor-pointer items-start gap-[10px]"
        >
          <ConsentCheckboxButton
            isChecked={transactionalSmsChecked}
            ariaLabel="Toggle transactional and informational SMS consent"
            onToggle={() => setTransactionalSmsChecked((prev) => !prev)}
          />
          <p className="text-left text-[12px] font-medium leading-[1.6] text-muted">
            By checking, you are allowing to receive{' '}
            <span className="text-primary">transactional/informational SMS</span> communications regarding account
            notifications, customer care, etc from <span className="text-primary">Conrad</span>. Messages frequency may
            vary. Data rates may apply. Reply <span className="text-primary">&quot;STOP&quot; to opt-out</span>.
            (optional)
          </p>
        </div>
        <div
          onClick={() => setMarketingSmsChecked((prev) => !prev)}
          className="flex cursor-pointer items-start gap-[10px]"
        >
          <ConsentCheckboxButton
            isChecked={marketingSmsChecked}
            ariaLabel="Toggle promotional and marketing SMS consent"
            onToggle={() => setMarketingSmsChecked((prev) => !prev)}
          />
          <p className="text-left text-[12px] font-medium leading-[1.6] text-muted">
            By checking, you are allowing to receive <span className="text-primary">promotional/marketing SMS</span>{' '}
            communications from <span className="text-primary">Conrad</span>. Frequency may vary. Data rates may apply,{' '}
            <span className="text-primary">reply &quot;HELP&quot; for help or &quot;STOP&quot; to opt-out</span>.
            (optional)
          </p>
        </div>
      </div>
    </div>
  )
}
